import {
  IonImg,
  IonLabel,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
  IonTabs,
  useIonToast,
} from '@ionic/react';
import { Redirect, Route, useHistory, useLocation } from 'react-router';
import HotListTab from 'pages/HotListTab';
import MyPlayDashboard from 'pages/MyPlayDashboard';
import NewsTab from 'pages/NewsTab';
import { useDispatch, useSelector } from 'react-redux';
import { IState } from 'appRedux/createStore';
import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Home } from './views/home';
import SlotoverviewTab from './views/slotDetail/SlotoverviewTab';
import { ListReportEnum } from 'common/common';
import moment from 'moment';
import { ICasinoSchema } from 'appRedux/models/casinoModels';
import { selectAuthStateTransformed } from 'appRedux/selectors';
import { ChooseCasinos } from 'pages/Signup/views/ChooseCasinos';
import EventsTab from 'pages/EventsTab';
import Challenges from 'pages/EventsTab/Challenges/Challenges';
import JoinChallenge from 'pages/EventsTab/Challenges/JoinChallenge';
import LeaderBoardTab from 'pages/LeaderBoardTab';
import { FOOTER_VISIBILITY_EVENT, FooterVisibilityEventDetail } from 'common/footerVisibility';
import { CasinoService } from 'services/casino';
import { CasinoReduxCommandCreator } from 'appRedux/actions/casinoCommandCreator';
import { canEnterCasino } from 'lib/casinoComingSoonAccess';
import { shouldShowPop } from 'lib/showPop';
import { shouldShowLoadingReels } from 'lib/showLoadingReels';
import { getLinkPlayerFlowVersion } from 'lib/linkPlayerFlow';
import { useCasinoTabBarWatermark } from 'lib/hasCasinoAccountRole';

interface IHandleTabsDidChange {
  detail: {
    tab: string;
  };
}

const TabRootPage = () => {
  const authState = useSelector(selectAuthStateTransformed);
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const casinoCommands = CasinoReduxCommandCreator(dispatch);
  const [selectedTab, setSelectedTab] = useState<string>('home');
  const [isFooterVisible, setIsFooterVisible] = useState(true);
  const [presentToast] = useIonToast();
  // Footer CASINO watermark: casino accounts only (display-only).
  const watermark = useCasinoTabBarWatermark();

  const handleTabsDidChange = (event: IHandleTabsDidChange) => {
    if (casinoSchema) {
      setSelectedTab(event.detail.tab);
    } else {
      setSelectedTab('casinos');
      presentToast({
        position: 'top',
        duration: 5000,
        color: 'danger',
        message: 'Please choose a casino to continue.',
      }).catch(() => null);
    }
  };
  const casinoSchema: ICasinoSchema = useSelector<IState>(
    (state) => state.app.persistedState.casinoSchema
  ) as ICasinoSchema;
  const casinoTimezoneNew = casinoSchema?.casinoTimezoneNew;

  // If a stored casino is Coming Soon and not preview-enterable (e.g. preview
  // group removed), clear selection and route to casino select. Server
  // GetAllCasinos is source of truth (isComingSoon kept; isPreview grants entry).
  useEffect(() => {
    if (!authState || !casinoSchema?.casinoId) return;
    const casinoSvc = new CasinoService();
    const sub = casinoSvc.GetAllCasinos().subscribe({
      next(resp) {
        const match = (resp?.DataSet || []).find(
          (c) =>
            c.casinoId === casinoSchema.casinoId ||
            c.kpCasinoPk === casinoSchema.kpCasinoPk
        );
        if (!match || !canEnterCasino(match)) {
          casinoCommands.SetCasinoSchema(null as unknown as ICasinoSchema);
          setSelectedTab('casinos');
          if (!location.pathname.includes('/tabs/casinos')) {
            history.replace('/tabs/casinos');
          }
          return;
        }
        // Keep preview badge / flags in sync with the latest server response.
        // showPOP is synced too so a casino's POP visibility follows Mongo on the
        // next app load (set explicitly so a removed field hides POP again).
        // showLoadingReels likewise (Explore slot-reel loading cards vs gray cards),
        // and LinkPlayerflowVersion (full-screen vs drawer Link Player ID).
        if (
          match.isPreview !== casinoSchema.isPreview ||
          match.isComingSoon !== casinoSchema.isComingSoon ||
          shouldShowPop(match) !== shouldShowPop(casinoSchema) ||
          shouldShowLoadingReels(match) !== shouldShowLoadingReels(casinoSchema) ||
          getLinkPlayerFlowVersion(match) !== getLinkPlayerFlowVersion(casinoSchema)
        ) {
          casinoCommands.SetCasinoSchema({
            ...casinoSchema,
            ...match,
            showPOP: match.showPOP,
            showLoadingReels: match.showLoadingReels,
            LinkPlayerflowVersion: match.LinkPlayerflowVersion,
          });
        }
      },
      error() {
        /* ignore — offline / transient; keep current schema */
      },
    });
    return () => sub.unsubscribe();
    // Only re-check when the selected casino id changes (or auth becomes ready).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState, casinoSchema?.casinoId]);

  const isLandscape = useSelector<IState>(
    (state) => state.app.persistedState.orientationState?.isLandscape
  ) as boolean;

  useEffect(() => {
    if (!location.pathname.includes('tabs')) {
      setSelectedTab('');
    }
    if (location.pathname.includes('/home')) {
      setSelectedTab('home');
    }
    if (location.pathname.includes('/tabs/casinos')) {
      setSelectedTab('casinos');
    }
    if (
      location.pathname.includes('/tabs/myPlay') ||
      location.pathname.includes('/tabs/myJackpots')
    ) {
      setSelectedTab('favorites');
    }
    if (
      location.pathname.includes('/tabs/events') ||
      location.pathname.includes('/tabs/challenges') ||
      location.pathname.includes('/tabs/join-challenge') ||
      location.pathname.includes('/tabs/leaderboard')
    ) {
      setSelectedTab('events');
    }
    // if (location.pathname.includes('/tabs/leaderboard')) {
    //   setSelectedTab('leaderboard');
    // }
    if (location.pathname.includes('/detail')) {
      setSelectedTab('');
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleFooterVisibility = (event: Event) => {
      const customEvent = event as CustomEvent<FooterVisibilityEventDetail>;
      setIsFooterVisible(customEvent.detail?.visible ?? true);
    };

    window.addEventListener(FOOTER_VISIBILITY_EVENT, handleFooterVisibility);
    return () => {
      window.removeEventListener(FOOTER_VISIBILITY_EVENT, handleFooterVisibility);
    };
  }, []);

  useEffect(() => {
    if (
      !location.pathname.includes('/tabs/home') &&
      !location.pathname.includes('/detail') &&
      !location.pathname.includes('/tabs/leaderboard')
    ) {
      setIsFooterVisible(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    moment.tz.setDefault(casinoTimezoneNew);
  }, [casinoTimezoneNew]);

  return authState ? (
    <IonTabs onIonTabsDidChange={handleTabsDidChange}>
      <IonRouterOutlet>
        <Redirect exact from="/tabs/favorites" to="/tabs/myPlay" />
        <Route path="/tabs/casinos" component={ChooseCasinos} />
        <Route path="/tabs/home" component={Home} />
        
        <Route
          path="/tabs/myPlay"
          render={() => <MyPlayDashboard />}
          exact
        />
        <Route
          path="/tabs/myJackpots"
          render={() => <HotListTab selectedPage={ListReportEnum.MyJackpot} />}
          exact
        />

        <Route path="/tabs/events" render={() => <EventsTab isDarkMode={false} />} />
        <Route path="/tabs/challenges" component={Challenges} />
        <Route path="/tabs/join-challenge/:challengeId" component={JoinChallenge} />
        <Route path="/tabs/leaderboard" component={LeaderBoardTab} />
        <Route path="/tabs/news" component={NewsTab} />
        <Route path="/detail" component={SlotoverviewTab} />
        <Redirect exact path="/" to="/tabs/home" />
      </IonRouterOutlet>
      <IonTabBar
        id="casino-tab-bar"
        className={clsx(
          'casino-tab-bar',
          { 'casino-tab-bar--hidden': !isFooterVisible },
          watermark.className
        )}
        // Drawn by ion-tab-bar.casino-tab-bar--watermark::before (_m-casino-tabs.scss).
        // IonTabBar only renders IonTabButton children, so the watermark cannot be a span.
        data-watermark={watermark.text}
        slot="bottom"
        style={{ display: isLandscape ? 'none' : 'flex' }}
      >
        <IonTabButton
          tab="casinos"
          href="/tabs/casinos"
          // style={
          //   {
          //     '--tab-icon-mask': "url('/assets/icon/tab-location-icon.svg')",
          //   } as React.CSSProperties
          // }
          className={clsx(selectedTab === 'casinos' ? 'selected' : 'unselected')}
        >
          <IonImg
            src={
              selectedTab === 'casinos'
                ? 'assets/icon/tab-casinos-selected.svg'
                : 'assets/icon/tab-location-icon.svg'
            }
          />
          <IonLabel>Casinos</IonLabel>
        </IonTabButton>

        <IonTabButton
          tab="home"
          href="/tabs/home"
          // style={
          //   { '--tab-icon-mask': "url('/assets/icon/tab-map-icon.svg')" } as React.CSSProperties
          // }
          className={clsx(
            selectedTab === 'home' ? 'selected' : 'unselected',
            !casinoSchema ? 'tab-disabled' : ''
          )}
        >
          <IonImg
            src={
              selectedTab === 'home'
                ? 'assets/icon/tab-explore-selected.svg'
                : 'assets/icon/tab-map-icon.svg'
            }
          />
          <IonLabel>Explore</IonLabel>
        </IonTabButton>
        <IonTabButton
          tab="favorites"
          href="/tabs/myPlay"
          // style={{ '--tab-icon-mask': "url('/assets/icon/tab-myplay.svg')" } as React.CSSProperties}
          className={clsx(
            selectedTab === 'favorites' ? 'selected' : 'unselected',
            !casinoSchema ? 'tab-disabled' : ''
          )}
        >
          <IonImg
            src={
              selectedTab === 'favorites'
                ? 'assets/icon/tab-myplay-selected.svg'
                : 'assets/icon/tab-myplay.svg'
            }
          />
          <IonLabel>My Play</IonLabel>
        </IonTabButton>
        {/* <IonTabButton
          tab="events"
          href="/tabs/events"
          className={clsx(
            selectedTab === 'events' ? 'selected' : 'unselected',
            !casinoSchema ? 'tab-disabled' : ''
          )}
        >
          <IonImg src="assets/icon/tab-leaderboard.svg" />
          <IonLabel>Events</IonLabel>
        </IonTabButton> */}
        <IonTabButton
          tab="events"
          href="/tabs/events"
          // style={
          //   { '--tab-icon-mask': "url('/assets/icon/tab-leaderboard.svg')" } as React.CSSProperties
          // }
          className={clsx(
            selectedTab === 'events' ? 'selected' : 'unselected',
            !casinoSchema ? 'tab-disabled' : ''
          )}
        >
          <IonImg
            src={
              selectedTab === 'events'
                ? 'assets/icon/tab-events-selected.svg'
                : 'assets/icon/tab-events.svg'
            }
          />
          <IonLabel>Events</IonLabel>
        </IonTabButton>
      </IonTabBar>
    </IonTabs>
  ) : (
    <Redirect to="/" />
  );
};

export default TabRootPage;
